/**
 * Liquidity (Tail Stock & Allocation Risk) — Figma 5917:3359
 *
 * Existing API today: liquiditySynopsis
 *   { liquid_stock_count, illiquid_stock_count,
 *     liquid_stock_weightage, illiquid_stock_weightage }
 *
 * Optional future keys (auto-used when present):
 *   corePositions, supportingPositions, tailPositions, noisePositions
 *   insight, recommendation, tableRows / positions
 */

export const LIQUIDITY_CARD_THEMES = {
  core: { topBorder: "#2879D8", mobileBorder: "#3B82F6", key: "core" },
  supporting: {
    topBorder: "#13AAD0",
    mobileBorder: "#6366F1",
    key: "supporting",
  },
  tail: { topBorder: "#637CEB", mobileBorder: "#EAB308", key: "tail" },
  noise: { topBorder: "#DFB70B", mobileBorder: "#A855F7", key: "noise" },
  liquidity: {
    topBorder: "#584AC1",
    mobileBorder: "#A855F7",
    key: "liquidity",
  },
};

export const ACTION_STYLES = {
  TRIM: { color: "#C40000", bg: "rgba(196,0,0,0.2)" },
  HOLD: { color: "#F6A823", bg: "rgba(246,168,35,0.2)" },
  IDEAL: { color: "#45AC70", bg: "rgba(69,172,112,0.2)" },
};

export const VS_IDEAL_COLORS = {
  negative: "#C40000",
  warning: "#F6A823",
  positive: "#45AC70",
  neutral: "#FFFFFF",
};

const formatPct = (value, digits = 1) => {
  if (value == null || value === "" || Number.isNaN(Number(value))) return "—";
  return `${Number(value).toFixed(digits)}%`;
};

const formatInr = (value) => {
  if (value == null || value === "" || Number.isNaN(Number(value))) return "—";
  return `₹${Number(value).toLocaleString("en-IN", {
    maximumFractionDigits: 0,
  })}`;
};

const getWeight = (stock) =>
  Number(stock?.weight ?? stock?.weightage) || 0;

/** Bucket rules aligned with Figma card copy */
export const getStockBucket = (weight) => {
  if (weight > 3) return "core";
  if (weight >= 1) return "supporting";
  if (weight >= 0.5) return "tail";
  return "noise";
};

const deriveVsIdealAndAction = (weight, idealCap = 12) => {
  if (weight == null || Number.isNaN(Number(weight))) {
    return {
      vsIdeal: "—",
      vsIdealTone: "neutral",
      action: null,
      riskTone: "neutral",
    };
  }
  const w = Number(weight);
  const diff = w - idealCap;

  if (diff > 5) {
    return {
      vsIdeal: `+${diff.toFixed(1)}% overweight`,
      vsIdealTone: "negative",
      action: "TRIM",
      riskTone: "negative",
    };
  }
  if (diff > 0.5) {
    return {
      vsIdeal: `+${diff.toFixed(1)}% overweight`,
      vsIdealTone: "warning",
      action: "HOLD",
      riskTone: "warning",
    };
  }
  if (diff > 0) {
    return {
      vsIdeal: `+${diff.toFixed(1)}% slight OW`,
      vsIdealTone: "warning",
      action: "HOLD",
      riskTone: "warning",
    };
  }
  return {
    vsIdeal: "On target",
    vsIdealTone: "positive",
    action: "IDEAL",
    riskTone: "positive",
  };
};

const formatVsIdealObject = (vsIdeal) => {
  if (vsIdeal == null || vsIdeal === "") return null;
  if (typeof vsIdeal !== "object") return String(vsIdeal);

  const deviation = vsIdeal.deviation;
  const label = vsIdeal.label || "";
  if (deviation == null || Number.isNaN(Number(deviation))) {
    return label || null;
  }

  const num = Number(deviation);
  const sign = num > 0 ? "+" : num < 0 ? "−" : "";
  const abs = Math.abs(num).toFixed(1);
  const labelText = label ? ` ${label}` : "";
  return `${sign}${abs}%${labelText}`.trim();
};

const toneFromVsIdeal = (vsIdeal, fallbackTone) => {
  if (vsIdeal && typeof vsIdeal === "object") {
    const label = String(vsIdeal.label || "").toLowerCase();
    const deviation = Number(vsIdeal.deviation);
    if (label.includes("overweight") && !label.includes("slight")) {
      return "negative";
    }
    if (label.includes("slight") || label.includes("underweight")) {
      return "warning";
    }
    if (label.includes("target") || label.includes("ideal")) {
      return "positive";
    }
    if (!Number.isNaN(deviation)) {
      if (Math.abs(deviation) > 5) return "negative";
      if (Math.abs(deviation) > 0.5) return "warning";
      return "positive";
    }
  }
  return fallbackTone;
};

const mapStockToRow = (stock, bucketWeightSum) => {
  const weight = getWeight(stock);
  const riskContribution =
    stock.risk_contribution ??
    stock.riskContribution ??
    (bucketWeightSum > 0 ? (weight / bucketWeightSum) * 100 : null);

  const derived = deriveVsIdealAndAction(
    weight,
    stock.ideal_weight ?? stock.idealWeight ?? 12,
  );

  const rawValue =
    stock.current_value ?? stock.currentValue ?? stock.value ?? null;
  const vsIdealRaw = stock.vs_ideal ?? stock.vsIdeal;
  const vsIdealFormatted = formatVsIdealObject(vsIdealRaw);

  return {
    stock: stock.symbol || stock.name || "—",
    sector: stock.sector || "—",
    weight,
    weightLabel: formatPct(weight),
    value: rawValue,
    valueLabel: formatInr(rawValue),
    vsIdeal: vsIdealFormatted || vsIdealRaw || derived.vsIdeal,
    vsIdealTone:
      stock.vs_ideal_tone ||
      stock.vsIdealTone ||
      toneFromVsIdeal(vsIdealRaw, derived.vsIdealTone),
    riskContribution,
    riskContributionLabel: formatPct(riskContribution),
    riskTone: stock.risk_tone || stock.riskTone || derived.riskTone,
    action: stock.action || derived.action,
  };
};

const normalizeApiBucket = (bucket) => {
  if (!bucket || typeof bucket !== "object") return null;
  return {
    count: bucket.count,
    subtitle: bucket.subtitle,
    capitalPercentage:
      bucket.capital_percentage ?? bucket.capitalPercentage ?? null,
    totalValue: bucket.total_value ?? bucket.totalValue ?? null,
    total_portfolio:
      bucket.total_portfolio ?? bucket.totalPortfolio ?? null,
    weightCondition:
      bucket.weight_condition ?? bucket.weightCondition ?? null,
    stocks: Array.isArray(bucket.stocks) ? bucket.stocks : [],
  };
};

const buildBucketSubtitle = (apiBucket, fallbackSubtitle) => {
  if (!apiBucket) return fallbackSubtitle;
  if (apiBucket.subtitle) return apiBucket.subtitle;

  const condition = apiBucket.weightCondition;
  const capital =
    apiBucket.capitalPercentage != null
      ? formatPct(apiBucket.capitalPercentage)
      : null;
  const totalValue =
    apiBucket.totalValue != null ? formatInr(apiBucket.totalValue) : null;

  if (condition && capital) {
    return `${condition} weight · ${capital} of capital`;
  }
  if (condition && totalValue) {
    return `${condition} weight · ${totalValue} total`;
  }
  return fallbackSubtitle;
};

const summarizeBucket = (stocks, { subtitleForNoise = false } = {}) => {
  const count = stocks.length;
  const capitalPct = stocks.reduce((sum, s) => sum + getWeight(s), 0);
  const totalValue = stocks.reduce(
    (sum, s) => sum + (Number(s.current_value) || 0),
    0,
  );

  return {
    count,
    capitalPct,
    totalValue,
    stocks,
    capitalLabel: formatPct(capitalPct),
    valueLabel: formatInr(totalValue),
    subtitleForNoise,
  };
};

/**
 * Build section model from liquiditySynopsis + stockAllocation (+ optional richer API).
 */
export const buildLiquiditySectionData = ({
  liquidityData,
  stockAllocation = [],
  apiSection,
} = {}) => {
  const stocks = Array.isArray(stockAllocation) ? stockAllocation : [];
  const api = apiSection && typeof apiSection === "object" ? apiSection : {};

  const buckets = {
    core: [],
    supporting: [],
    tail: [],
    noise: [],
  };

  stocks.forEach((stock) => {
    const key = getStockBucket(getWeight(stock));
    buckets[key].push(stock);
  });

  // Sort each bucket by weight desc
  Object.keys(buckets).forEach((key) => {
    buckets[key].sort((a, b) => getWeight(b) - getWeight(a));
  });

  const coreSummary = summarizeBucket(buckets.core);
  const supportingSummary = summarizeBucket(buckets.supporting);
  const tailSummary = summarizeBucket(buckets.tail);
  const noiseSummary = summarizeBucket(buckets.noise, {
    subtitleForNoise: true,
  });

  const illiquid = Number(liquidityData?.illiquid_stock_count) || 0;
  const liquid = Number(liquidityData?.liquid_stock_count) || 0;
  const totalLiquidityStocks = illiquid + liquid;

  const coreApi = normalizeApiBucket(api.core_positions || api.corePositions);
  const supportingApi = normalizeApiBucket(
    api.supporting_positions || api.supportingPositions,
  );
  const tailApi = normalizeApiBucket(api.tail_positions || api.tailPositions);
  const noiseApi = normalizeApiBucket(
    api.noise_positions || api.noisePositions,
  );
  const liquidityApi = normalizeApiBucket(
    api.liquidity_risk || api.liquidityRisk || api.liquidity,
  );

  const cards = [
    {
      key: "core",
      label: "CORE POSITIONS",
      value: coreApi?.count ?? coreSummary.count,
      subtitle: buildBucketSubtitle(
        coreApi,
        `>3% weight · ${coreSummary.capitalLabel} of capital`,
      ),
      themeKey: "core",
      stocks: coreApi ? coreApi.stocks : buckets.core,
    },
    {
      key: "supporting",
      label: "SUPPORTING POSITIONS",
      value: supportingApi?.count ?? supportingSummary.count,
      subtitle: buildBucketSubtitle(
        supportingApi,
        `1–3% weight · ${supportingSummary.capitalLabel} of capital`,
      ),
      themeKey: "supporting",
      stocks: supportingApi ? supportingApi.stocks : buckets.supporting,
    },
    {
      key: "tail",
      label: "TAIL POSITIONS",
      value: tailApi?.count ?? tailSummary.count,
      subtitle: buildBucketSubtitle(
        tailApi,
        `<1% weight · ${tailSummary.capitalLabel} of capital`,
      ),
      themeKey: "tail",
      stocks: tailApi ? tailApi.stocks : buckets.tail,
    },
    {
      key: "noise",
      label: "NOISE POSITIONS",
      value: noiseApi?.count ?? noiseSummary.count,
      subtitle: buildBucketSubtitle(
        noiseApi,
        `<0.5% weight · ${noiseSummary.valueLabel} total`,
      ),
      themeKey: "noise",
      stocks: noiseApi ? noiseApi.stocks : buckets.noise,
    },
    {
      key: "liquidity",
      label: "LIQUIDITY",
      value: liquidityApi?.count ?? illiquid,
      subtitle:
        liquidityApi?.subtitle ||
        (liquidityApi &&
        liquidityApi.count != null &&
        liquidityApi.total_portfolio != null
          ? `${liquidityApi.count} stocks out of ${liquidityApi.total_portfolio} face Liquidity Risk`
          : totalLiquidityStocks > 0
            ? `${illiquid} stocks out of ${totalLiquidityStocks} face Liquidity Risk`
            : "—"),
      themeKey: "liquidity",
      stocks: liquidityApi ? liquidityApi.stocks : stocks,
    },
  ];

  const buildRowsForCard = (card) => {
    if (Array.isArray(api.tableRows) && api.selectedCard === card.key) {
      return api.tableRows;
    }
    const list = card.stocks || [];
    const weightSum = list.reduce((sum, s) => sum + getWeight(s), 0);
    return list.map((s) => mapStockToRow(s, weightSum));
  };

  const buildInsight = (card) => {
    if (api.insight || api.recommendation) {
      return {
        body: api.insight || "",
        recommendation: api.recommendation || "",
      };
    }

    if (!card || card.key === "liquidity") {
      const wt = liquidityData?.illiquid_stock_weightage;
      if (totalLiquidityStocks <= 0) {
        return { body: "", recommendation: "" };
      }
      return {
        body: `${illiquid} stocks out of ${totalLiquidityStocks} face liquidity risk based on average traded value${
          wt != null
            ? `. About ${formatPct(wt)} of portfolio value may have liquidity issues`
            : ""
        }.`,
        recommendation:
          "Recommendation: review illiquid names and reduce exposure where exit risk is elevated.",
      };
    }

    const rows = buildRowsForCard(card);
    const top3 = rows.slice(0, 3);
    const top3Names = top3.map((r) => r.stock).filter(Boolean);
    const top3Weight = top3.reduce((sum, r) => sum + (Number(r.weight) || 0), 0);
    const largest = rows[0];
    const second = rows[1];

    return {
      body: `These ${card.value} positions are doing ${
        card.key === "core" ? "nearly all the work" : "meaningful work in this tier"
      }.${
        top3Names.length
          ? ` The top ${top3Names.length} (${top3Names.join(", ")}) account for ${formatPct(
              top3Weight,
            )} of NAV.`
          : ""
      }${
        largest && second
          ? ` Internal sizing disparity — particularly ${largest.stock} at ${largest.weightLabel} vs the next at ${second.weightLabel} — can create return dependency.`
          : ""
      }`,
      recommendation: api.recommendation || "",
    };
  };

  return { cards, buildRowsForCard, buildInsight };
};

